<template>
  <div :key="refreshKey" class="flex flex-col gap-5 dark:text-gray-200">
    <VideoDropdownModal v-model:open="showVideoModal" :video-url="videoURL" />
    <!-- Header Section -->
    <div
      class="relative flex flex-col w-full h-min text-main-700 bg-white rounded-lg bg-clip-border p-4 gap-5 dark:bg-gray-800"
    >
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5"
      >
        <!-- Back Button and Title -->
        <div
          class="w-full max-w-fit flex flex-row gap-5 pr-5 items-center justify-start"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-bluegray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600"
            aria-label="Go back"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <div>
            <h3
              class="block text-xl antialiased font-medium leading-snug tracking-normal dark:text-gray-200"
            >
              Customer List
            </h3>
            <p
              class="text-[14px] font-normal leading-6 text-gray-800 opacity-80 dark:text-gray-400"
            >
              See details about your customers and follow up on them.
            </p>
          </div>
        </div>

        <!-- Search and Export Button -->
        <div
          class="flex flex-col md:flex-row justify-end items-center gap-4 w-full"
        >
          <!-- Search Bar -->
          <div class="w-full md:max-w-sm min-w-[200px] relative">
            <div class="relative w-full">
              <input
                type="text"
                placeholder="Search for customer"
                class="peer pl-3 outline outline-0 focus:outline-0 pr-11 dark:bg-gray-700 dark:placeholder-gray-400 py-3 h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-500 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-500 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700"
                aria-label="Search for customer"
              />
              <button
                class="absolute h-8 w-8 right-1.5 top-1.5 my-auto px-2 flex items-center bg-main-800 rounded-lg hover:bg-gray-700 dark:bg-sub-900 dark:hover:bg-sub-500"
                type="button"
                aria-label="Search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="#FFF"
                  class="w-8 h-8"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <!-- Export Button -->
          <button
            class="flex items-center gap-3 portrait:w-full align-middle select-none font-semibold justify-center text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-2 px-4 rounded-lg bg-button text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none dark:bg-sub-900 dark:hover:bg-gray-500"
            type="button"
            aria-label="Export"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              aria-hidden="true"
              class="w-4 h-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              ></path>
            </svg>
            Export
          </button>
        </div>
      </nav>
    </div>

    <!-- Loading Indicator -->

    <div
      v-if="loading"
      class="w-full flex flex-col justify-center items-center h-20 mt-6 mb-16"
    >
      <LoadingComponent />
    </div>

    <!-- Table Section -->
    <div
      v-else
      :key="refreshKey"
      class="relative flex flex-col w-full h-full text-gray-700 bg-white shadow-md rounded-lg bg-clip-border overflow-auto dark:bg-gray-800 dark:text-gray-200"
    >
      <!-- Delete and Send Email Buttons -->
      <div
        v-if="selectedCustomers.length > 0"
        class="flex flex-row justify-start items-center gap-4 w-full pl-3 py-4 transition-opacity duration-300"
      >
        <button
          @click="deleteSelectedCustomers"
          class="relative z-10 align-middle text-sm portrait:text-sm py-2 px-4 rounded-lg font-medium text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-gray-800 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
        >
          <i class="fa-solid fa-trash text-xl"></i>
          Delete <span class="text-sm">({{ selectedCustomers.length }})</span>
        </button>
        <button
          @click="sendEmailToSelectedCustomers"
          class="relative z-10 align-middle text-sm portrait:text-sm py-2 px-4 rounded-lg font-medium text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-gray-800 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 gap-2 flex justify-between items-center"
        >
          <i class="fa-solid fa-envelope-circle-check text-xl"></i>
          Send Email
          <span class="text-sm">({{ selectedCustomers.length }})</span>
        </button>
      </div>

      <!-- Table -->
      <div class="w-full h-full overflow-auto">
        <table class="w-full text-left table-auto min-w-max rounded-lg">
          <thead>
            <tr
              class="p-4 border-y border-blue-gray-100 bg-blue-gray-50/50 dark:bg-gray-700"
            >
              <th
                class="p-4 border-y border-blue-gray-100 dark:border-gray-600"
              >
                <div class="flex items-center gap-3">
                  <label
                    class="relative flex items-center rounded-full cursor-pointer"
                    htmlFor="selectAll"
                  >
                    <input
                      type="checkbox"
                      class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-500 dark:checked:border-gray-200 dark:checked:bg-gray-200"
                      id="selectAll"
                      @change="selectAllCustomers"
                      :checked="selectedCustomers.length === customers.length"
                      aria-label="Select all customers"
                    />
                    <span
                      class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100 dark:text-gray-800"
                    >
                      <i class="fa-solid fa-check"></i>
                    </span>
                  </label>
                  <p
                    class="text-start text-sm md:text-base font-medium leading-none text-gray-800 dark:text-gray-200"
                  >
                    Customer
                  </p>
                </div>
              </th>
              <th
                class="p-4 border-y border-blue-gray-100 dark:border-gray-600"
              >
                <p
                  class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-gray-200"
                >
                  No Orders
                </p>
              </th>
              <th
                class="p-4 border-y border-blue-gray-100 dark:border-gray-600"
              >
                <p
                  class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-gray-200"
                >
                  Phone
                </p>
              </th>
              <th
                class="p-4 border-y border-blue-gray-100 dark:border-gray-600"
              >
                <p
                  class="block text-sm antialiased font-normal leading-none text-blue-gray-900 dark:text-gray-200"
                ></p>
              </th>
            </tr>
          </thead>

          <tbody>
            <!-- No Customers Message -->
            <tr v-if="!loading && customers.length === 0">
              <td colspan="6" class="p-6 text-center">
                <div
                  class="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible"
                >
                  <svg
                    class="w-16 h-16 text-gray-400 dark:text-gray-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                  >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M9.25 4C9.25 2.48122 10.4812 1.25 12 1.25C13.5188 1.25 14.75 2.48122 14.75 4C14.75 5.51878 13.5188 6.75 12 6.75C10.4812 6.75 9.25 5.51878 9.25 4Z"
                        fill="currentColor"
                      ></path>
                      <path
                        d="M8.22309 11.5741L6.04779 10.849C5.42206 10.6404 5 10.0548 5 9.39526C5 8.41969 5.89953 7.69249 6.85345 7.89691L8.75102 8.30353C8.85654 8.32614 8.9093 8.33744 8.96161 8.34826C10.966 8.76286 13.034 8.76286 15.0384 8.34826C15.0907 8.33744 15.1435 8.32614 15.249 8.30353L17.1465 7.8969C18.1005 7.69249 19 8.41969 19 9.39526C19 10.0548 18.5779 10.6404 17.9522 10.849L15.7769 11.5741C15.514 11.6617 15.3826 11.7055 15.2837 11.7666C14.9471 11.9743 14.7646 12.361 14.8182 12.753C14.834 12.8681 14.8837 12.9974 14.9832 13.256L16.23 16.4977C16.6011 17.4626 15.8888 18.4997 14.8549 18.4997C14.3263 18.4997 13.8381 18.2165 13.5758 17.7574L12 14.9997L10.4242 17.7574C10.1619 18.2165 9.67373 18.4997 9.14506 18.4997C8.11118 18.4997 7.39889 17.4626 7.77003 16.4977L9.01682 13.256C9.11629 12.9974 9.16603 12.8681 9.18177 12.753C9.23536 12.361 9.05287 11.9743 8.71625 11.7666C8.61741 11.7055 8.48597 11.6617 8.22309 11.5741Z"
                        fill="currentColor"
                      ></path>
                      <path
                        d="M12 21.9998C17.5228 21.9998 22 19.9851 22 17.4998C22 15.778 19.8509 14.282 16.694 13.5254L17.63 15.959C18.379 17.9065 16.9415 19.9996 14.8549 19.9996C13.788 19.9996 12.8028 19.4279 12.2735 18.5015L12 18.0229L11.7265 18.5015C11.1972 19.4279 10.212 19.9996 9.14506 19.9996C7.05851 19.9996 5.62099 17.9065 6.37001 15.959L7.30603 13.5254C4.14907 14.282 2 15.778 2 17.4998C2 19.9851 6.47715 21.9998 12 21.9998Z"
                        fill="currentColor"
                      ></path>
                    </g>
                  </svg>
                  <p class="text-lg font-semibold dark:text-gray-300">
                    No Customers yet!
                  </p>
                  <p class="text-sm dark:text-gray-400">
                    Don't worry, just make sure your store is on auto-pilot.
                  </p>
                </div>
              </td>
            </tr>

            <!-- Customers List -->
            <tr
              v-for="customer in customers"
              :key="customer.id"
              class="border-b border-main-50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
            >
              <td class="p-4 border-b border-blue-gray-50 dark:border-gray-600">
                <div class="flex items-center gap-3">
                  <label
                    class="relative flex items-center rounded-full cursor-pointer"
                    :for="`checkbox-${customer.id}`"
                  >
                    <input
                      type="checkbox"
                      class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 hover:before:opacity-10 dark:border-gray-500 dark:checked:border-gray-200 dark:checked:bg-gray-200"
                      :id="`checkbox-${customer.id}`"
                      v-model="selectedCustomers"
                      :value="customer.id"
                      aria-label="Select customer"
                    />
                    <span
                      class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100 dark:text-gray-800"
                    >
                      <i class="fa-solid fa-check"></i>
                    </span>
                  </label>
                  <div class="flex flex-col">
                    <RouterLink
                      :to="`/customer-details/${customer.id}`"
                      class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 underline md:no-underline md:hover:underline dark:text-gray-200"
                    >
                      {{ customer.name }}
                    </RouterLink>
                    <p
                      class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 opacity-70 dark:text-gray-400"
                    >
                      {{ customer.email }}
                    </p>
                  </div>
                </div>
              </td>
              <td class="p-4 border-b border-blue-gray-50 dark:border-gray-600">
                <div class="flex flex-col">
                  <p
                    class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 dark:text-gray-200"
                  >
                    {{ customer.orders }}
                  </p>
                  <p
                    class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 opacity-70 dark:text-gray-400"
                  >
                    Orders
                  </p>
                </div>
              </td>
              <td class="p-4 border-b border-blue-gray-50 dark:border-gray-600">
                <div class="flex flex-col w-max gap-1">
                  <!-- WhatsApp Link -->
                  <a
                    :href="`https://wa.me/${customer.phone}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 dark:text-gray-200"
                  >
                    {{ customer.phone }}
                  </a>
                  <a
                    :href="`https://wa.me/${customer.phone}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="relative flex gap-3 items-center px-2 py-1 text-xs font-bold text-green-900 uppercase rounded-md select-none whitespace-nowrap bg-green-500/10 hover:bg-green-500/20 w-max dark:bg-green-500/20 dark:text-green-300"
                  >
                    Chat <i class="fa-brands fa-whatsapp text-xl"></i>
                  </a>
                </div>
              </td>
              <td class="p-4 border-b border-blue-gray-50 dark:border-gray-600">
                <!-- Email Link -->
                <a
                  :href="`mailto:${customer.email}`"
                  class="relative h-10 max-h-[40px] w-max select-none rounded-lg text-center align-middle text-xs font-medium uppercase text-sub-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none flex flex-col items-start justify-center dark:text-gray-200"
                  type="button"
                >
                  <i class="fa-solid fa-envelope-circle-check text-xl"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        class="flex items-center justify-between p-4 border-t border-blue-gray-50 dark:border-gray-600"
      >
        <p
          class="block text-sm antialiased font-normal leading-normal text-blue-gray-900 dark:text-gray-200"
        >
          Page {{ currentPage }} of {{ totalPages }}
        </p>
        <div class="flex gap-2">
          <button
            @click="goToPreviousPage"
            :disabled="currentPage === 1"
            class="select-none rounded-lg border border-gray-900 py-2 px-4 text-center align-middle text-xs font-bold uppercase text-gray-900 transition-all hover:opacity-75 focus:ring focus:ring-gray-300 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:border-gray-200 dark:text-gray-200"
          >
            Previous
          </button>
          <button
            @click="goToNextPage"
            :disabled="currentPage === totalPages"
            class="select-none rounded-lg border border-gray-900 py-2 px-4 text-center align-middle text-xs font-bold uppercase text-gray-900 transition-all hover:opacity-75 focus:ring focus:ring-gray-300 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:border-gray-200 dark:text-gray-200"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { mapActions } from "vuex";
import eventBus from "@/utils/eventBus";

export default {
  name: "CustomersView",
  data() {
    return {
      selectedCustomers: [],
      loading: false,
      customers: [],
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      showVideoModal: false,
      videoURL: "https://www.youtube.com/watch?v=9yYBdOaG7Tc",
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
    };
  },
  provide() {
    return {
      helpVideoURL: this.videoURL,
    };
  },
  watch: {
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
        this.fetchCustomers();
      },
    },
  },
  beforeUnmount() {
    eventBus.off("open-help-video", this.handleOpenVideo);
  },
  mounted() {
    eventBus.on("open-help-video", this.handleOpenVideo);
  },
  computed: {
    paginatedCustomers() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      return this.customers.slice(startIndex, startIndex + this.pageSize);
    },
  },
  methods: {
    ...mapActions(["getAllCustomers"]),
    handleOpenVideo() {
      this.showVideoModal = true;
    },
    goback() {
      this.$router.back();
    },
    async fetchCustomers() {
      this.loading = true;
      try {
        const response = await this.getAllCustomers();
        if (response.success) {
          this.customers = response.customers;
          console.log(this.customers, "customers");
        } else {
          toast.error(response.error || "Failed to fetch orders");
        }
      } catch (error) {
        toast.error("Error fetching orders");
      } finally {
        this.loading = false;
      }
    },
    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    selectAllCustomers(event) {
      if (event.target.checked) {
        this.selectedCustomers = this.customers.map((customer) => customer.id);
      } else {
        this.selectedCustomers = [];
      }
    },
    deleteSelectedCustomers() {
      console.log("Deleting:", this.selectedCustomers);
    },
    sendEmailToSelectedCustomers() {
      console.log("Sending email to:", this.selectedCustomers);
    },
  },
  created() {
    this.fetchCustomers();
  },
};
</script>
